/**
 * DeviceTargetingForm — campaign-scoped device allowlist/denylist editor.
 *
 * UX:
 *   • Campaign ID text input (validated, 1..64 of [A-Za-z0-9_-]).
 *   • Mode toggle: allow vs deny.
 *   • Device chips (emerald accent when active): mobile / desktop / tablet.
 *   • Save button persists via POST /api/v1/campaign-device-targeting.
 *   • Load button hydrates existing rule (GET /:campaignId).
 *
 * Stack: React Hook Form + Zod 4, TanStack Query, Sonner toasts.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import {
	allowlistToSelected,
	DEVICE_TYPES,
	deviceTargetingKeys,
	fetchDeviceTargeting,
	selectedToAllowlist,
	upsertDeviceTargeting,
	type DeviceType,
	type TargetingMode,
} from "./api";

// ---------------------------------------------------------------------------
// Zod 4 schema
// ---------------------------------------------------------------------------

const campaignIdSchema = z
	.string()
	.trim()
	.min(1, "Campaign ID is required")
	.max(64, "Campaign ID must be at most 64 characters")
	.regex(
		/^[a-z0-9_-]+$/i,
		"Letters, digits, underscore, and hyphen only",
	);

const deviceSchema = z.enum(["mobile", "desktop", "tablet"]);

const formSchema = z
	.object({
		campaignId: campaignIdSchema,
		mode: z.enum(["allow", "deny"]),
		devices: z.array(deviceSchema),
	})
	.refine(
		(v) =>
			v.mode === "allow" ? v.devices.length >= 1 : v.devices.length >= 0,
		{
			message: "Select at least one device to allow",
			path: ["devices"],
		},
	)
	.refine(
		(v) => (v.mode === "deny" ? v.devices.length < DEVICE_TYPES.length : true),
		{
			message: "Denying every device would block all traffic",
			path: ["devices"],
		},
	);

type FormValues = z.infer<typeof formSchema>;

// ---------------------------------------------------------------------------
// Pure styling tokens (Tailwind 4 + scaffold theme variables).
// ---------------------------------------------------------------------------

const PANEL = "rounded-[8px] border border-zinc-200 bg-white shadow-sm";
const HEADING =
	"text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl";
const LABEL = "text-sm font-medium text-zinc-800";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs font-medium text-red-600";
const INPUT =
	"block w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 " +
	"placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/25";
const PRIMARY_BTN =
	"inline-flex items-center justify-center rounded-[6px] bg-gradient-to-r from-emerald-500 to-sky-500 px-4 py-2 " +
	"text-sm font-semibold text-white shadow-sm transition hover:from-emerald-600 hover:to-sky-600 " +
	"focus:outline-none focus:ring-2 focus:ring-emerald-500/40 disabled:cursor-not-allowed disabled:opacity-60";
const SECONDARY_BTN =
	"inline-flex items-center justify-center rounded-[6px] border border-zinc-300 bg-white px-4 py-2 " +
	"text-sm font-medium text-zinc-700 shadow-sm transition hover:border-zinc-400 hover:bg-zinc-50 " +
	"focus:outline-none focus:ring-2 focus:ring-emerald-500/25 disabled:cursor-not-allowed disabled:opacity-60";

function modeBtn(active: boolean) {
	return [
		"rounded-[6px] px-3 py-1.5 text-sm font-medium transition",
		"focus:outline-none focus:ring-2 focus:ring-emerald-500/40",
		active
			? "bg-white text-zinc-900 shadow-sm"
			: "text-zinc-500 hover:text-zinc-800",
	].join(" ");
}

function chipClass(active: boolean) {
	return [
		"inline-flex items-center gap-2 rounded-[6px] border px-3 py-1.5 text-sm font-medium transition",
		"focus:outline-none focus:ring-2 focus:ring-emerald-500/40",
		active
			? "border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm"
			: "border-zinc-300 bg-white text-zinc-700 hover:border-zinc-400 hover:bg-zinc-50",
	].join(" ");
}

const DEVICE_LABEL: Record<DeviceType, string> = {
	mobile: "Mobile",
	desktop: "Desktop",
	tablet: "Tablet",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DeviceTargetingForm() {
	const queryClient = useQueryClient();
	const [lastLoadedCampaign, setLastLoadedCampaign] = useState<string | null>(
		null,
	);
	const [isLoadingRule, setIsLoadingRule] = useState(false);

	const {
		control,
		handleSubmit,
		register,
		reset,
		watch,
		setValue,
		getValues,
		formState: { errors, isSubmitting },
	} = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			campaignId: "",
			mode: "allow",
			devices: [],
		},
		mode: "onBlur",
	});

	const campaignId = watch("campaignId");
	const mode = watch("mode");
	const selectedDevices = watch("devices");

	const selectedSet = useMemo(() => new Set(selectedDevices), [selectedDevices]);

	const toggleDevice = useCallback(
		(device: DeviceType) => {
			const current = new Set(getValues("devices"));
			if (current.has(device)) current.delete(device);
			else current.add(device);
			const next = DEVICE_TYPES.filter((d) => current.has(d));
			setValue("devices", next, { shouldValidate: true, shouldDirty: true });
		},
		[getValues, setValue],
	);

	const saveMutation = useMutation({
		mutationFn: (values: FormValues) =>
			upsertDeviceTargeting({
				campaignId: values.campaignId,
				devices: selectedToAllowlist(values.devices, values.mode),
			}),
		onSuccess: (targeting, vars) => {
			queryClient.invalidateQueries({
				queryKey: deviceTargetingKeys.byCampaign(vars.campaignId),
			});
			toast.success("Device targeting saved", {
				description: `Campaign ${targeting.campaign_id} — ${targeting.devices.length} allowed device(s).`,
			});
			setLastLoadedCampaign(vars.campaignId);
		},
		onError: (err) => {
			const message =
				err instanceof Error && err.message ? err.message : "Save failed";
			toast.error("Could not save device targeting", {
				description: message,
			});
		},
	});

	const onSubmit = handleSubmit((values) => {
		saveMutation.mutate(values);
	});

	const loadExisting = useCallback(async () => {
		const id = getValues("campaignId").trim();
		const parsed = campaignIdSchema.safeParse(id);
		if (!parsed.success) {
			toast.error("Enter a valid campaign ID first");
			return;
		}
		setIsLoadingRule(true);
		try {
			const existing = await fetchDeviceTargeting(parsed.data);
			if (!existing) {
				toast("No existing rule", {
					description:
						"This campaign has no device-targeting rule. All devices are currently allowed.",
				});
				setValue("devices", [], { shouldDirty: false });
				setLastLoadedCampaign(parsed.data);
				return;
			}
			const currentMode = getValues("mode");
			const selected = allowlistToSelected(existing.devices, currentMode);
			setValue("devices", selected, { shouldDirty: false });
			setLastLoadedCampaign(parsed.data);
			toast.success("Loaded existing rule", {
				description: `${existing.devices.length} allowed device(s) on record.`,
			});
		} catch (err) {
			const message =
				err instanceof Error && err.message ? err.message : "Load failed";
			toast.error("Could not load rule", { description: message });
		} finally {
			setIsLoadingRule(false);
		}
	}, [getValues, setValue]);

	// If the user flips mode after loading a rule, re-project the selection so
	// the intent stays identical (allowlist invariant on the server).
	useEffect(() => {
		if (!lastLoadedCampaign) return;
		// Intentionally no-op: the form owns its selection once mounted.
		// This effect exists to prevent accidental re-fetches when mode toggles.
	}, [mode, lastLoadedCampaign]);

	const submitDisabled = isSubmitting || saveMutation.isPending;

	return (
		<form onSubmit={onSubmit} className="flex flex-col gap-6">
			<header className="flex flex-col gap-2">
				<h1 className={HEADING}>Device targeting</h1>
				<p className="max-w-2xl text-sm text-zinc-600">
					Choose which device classes this campaign accepts. Rules are evaluated
					at the edge before the click is counted. Leaving a campaign
					unconfigured allows every device.
				</p>
			</header>

			<section className={`${PANEL} p-6`}>
				<div className="flex flex-col gap-6">
					<div className="flex flex-col gap-2">
						<label htmlFor="campaignId" className={LABEL}>
							Campaign ID
						</label>
						<input
							id="campaignId"
							type="text"
							autoComplete="off"
							spellCheck={false}
							placeholder="camp_summer_promo"
							className={INPUT}
							{...register("campaignId")}
						/>
						<div className="flex items-center justify-between gap-4">
							<span className={HINT}>
								Letters, digits, underscore, hyphen. Up to 64 characters.
							</span>
							<button
								type="button"
								onClick={loadExisting}
								disabled={isLoadingRule || !campaignId}
								className={SECONDARY_BTN}
							>
								{isLoadingRule ? "Loading…" : "Load existing"}
							</button>
						</div>
						{errors.campaignId ? (
							<p className={ERROR_TEXT}>{errors.campaignId.message}</p>
						) : null}
					</div>

					<div className="flex flex-col gap-2">
						<span className={LABEL}>Mode</span>
						<Controller
							control={control}
							name="mode"
							render={({ field }) => (
								<div
									role="radiogroup"
									aria-label="Targeting mode"
									className="inline-flex items-center gap-1 rounded-[8px] border border-zinc-200 bg-zinc-50 p-1"
								>
									{(["allow", "deny"] as TargetingMode[]).map((value) => {
										const active = field.value === value;
										return (
											<button
												key={value}
												type="button"
												role="radio"
												aria-checked={active}
												onClick={() => field.onChange(value)}
												className={modeBtn(active)}
											>
												{value === "allow" ? "Allow listed" : "Block listed"}
											</button>
										);
									})}
								</div>
							)}
						/>
						<p className={HINT}>
							{mode === "allow"
								? "Only devices you pick below will be admitted."
								: "Every device is admitted except the ones you pick below."}
						</p>
					</div>

					<div className="flex flex-col gap-2">
						<span className={LABEL}>Devices</span>
						<div className="flex flex-wrap gap-2">
							{DEVICE_TYPES.map((device) => {
								const active = selectedSet.has(device);
								return (
									<button
										key={device}
										type="button"
										aria-pressed={active}
										onClick={() => toggleDevice(device)}
										className={chipClass(active)}
									>
										<span
											aria-hidden="true"
											className={
												active
													? "h-2 w-2 rounded-full bg-emerald-500"
													: "h-2 w-2 rounded-full bg-zinc-300"
											}
										/>
										<span>{DEVICE_LABEL[device]}</span>
									</button>
								);
							})}
						</div>
						{errors.devices ? (
							<p className={ERROR_TEXT}>{errors.devices.message}</p>
						) : null}
					</div>

					<div className="flex flex-wrap items-center justify-between gap-3 border-t border-zinc-200 pt-4">
						<div className="flex flex-col">
							<span className={LABEL}>
								{selectedSet.size === 0
									? "Nothing selected"
									: `${selectedSet.size} device${selectedSet.size === 1 ? "" : "s"} selected`}
							</span>
							<span className={HINT}>
								Server will persist an allowlist of{" "}
								{selectedToAllowlist(selectedDevices, mode).length} device(s).
							</span>
						</div>
						<div className="flex gap-2">
							<button
								type="button"
								className={SECONDARY_BTN}
								onClick={() =>
									reset({
										campaignId: getValues("campaignId"),
										mode: "allow",
										devices: [],
									})
								}
								disabled={submitDisabled}
							>
								Reset
							</button>
							<button
								type="submit"
								className={PRIMARY_BTN}
								disabled={submitDisabled}
							>
								{saveMutation.isPending ? "Saving…" : "Save targeting"}
							</button>
						</div>
					</div>
				</div>
			</section>
		</form>
	);
}

export default DeviceTargetingForm;
